import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidIsbn } from "@passa-a-pagina/shared";

interface Props {
  onDetected: (isbn: string) => void;
  onClose: () => void;
}

/**
 * Camera-based ISBN scanner using @zxing/browser. Requests the rear camera
 * via getUserMedia constraints — works on mobile where enumerateDevices()
 * returns empty labels until a permission has been granted.
 */
export function IsbnScanner({ onDetected, onClose }: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t("admin.scanner.noCamera"));
        return;
      }
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader();
        const constraints: MediaStreamConstraints = {
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current!,
          (result) => {
            if (result) {
              const text = result.getText().replace(/[-\s]/g, "").toUpperCase();
              if (isValidIsbn(text)) {
                onDetected(text);
                controls.stop();
              }
            }
          },
        );
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
      } catch (err) {
        const msg = (err as Error).message ?? t("admin.scanner.startError");
        if (/denied|permission|notallowed/i.test(msg)) {
          setError(t("admin.scanner.denied"));
        } else if (/notfound|not found|devicenotfound/i.test(msg)) {
          setError(t("admin.scanner.notFound"));
        } else if (/secure|https/i.test(msg)) {
          setError(t("admin.scanner.https"));
        } else {
          setError(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [onDetected, t]);

  return (
    <div className="scanner" role="dialog" aria-modal="true" aria-label={t("admin.scanner.dialogAria")}>
      <div className="scanner__viewport">
        <video ref={videoRef} aria-label={t("admin.scanner.videoAria")} muted playsInline autoPlay />
        <div className="scanner__crosshair" aria-hidden="true" />
      </div>
      {error && (
        <p className="scanner__error" role="alert">
          {error}
        </p>
      )}
      <div className="scanner__actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t("admin.scanner.close")}
        </button>
      </div>
    </div>
  );
}
